// frontend/src/constants/notifications.ts - ENHANCED SOUND SYSTEM

// Sound enabled key
export const SOUND_ENABLED_KEY = 'notification_sound_enabled';

// Sound mapping - using reliable external sounds with better CORS support
export const SOUNDS: Record<string, string> = {
  // Using more reliable sound files with fallbacks
  order_accepted: 'https://www.soundjay.com/misc/sounds/bell-ringing-05.mp3',
  offer_accepted: 'https://www.soundjay.com/misc/sounds/bell-ringing-05.mp3',
  payment_received: 'https://www.soundjay.com/misc/sounds/bell-ringing-05.mp3',
  service_started: 'https://www.soundjay.com/misc/sounds/bell-ringing-05.mp3',
  destination_reached: 'https://www.soundjay.com/misc/sounds/bell-ringing-05.mp3',
  order_completed: 'https://www.soundjay.com/misc/sounds/bell-ringing-05.mp3',
  new_message: 'https://www.soundjay.com/misc/sounds/bell-ringing-05.mp3',
  offer_created: 'https://www.soundjay.com/misc/sounds/bell-ringing-05.mp3',
  system: 'https://www.soundjay.com/misc/sounds/bell-ringing-05.mp3',
  error: 'https://www.soundjay.com/misc/sounds/bell-ringing-05.mp3',
  success: 'https://www.soundjay.com/misc/sounds/bell-ringing-05.mp3',
  warning: 'https://www.soundjay.com/misc/sounds/bell-ringing-05.mp3',
  info: 'https://www.soundjay.com/misc/sounds/bell-ringing-05.mp3',
};

// Local fallback sounds (base64 encoded beeps)
export const FALLBACK_SOUNDS = {
  beep: 'data:audio/wav;base64,UklGRlwAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YVAAAAA8Pzw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PA==',
  notification: 'data:audio/wav;base64,UklGRlwAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YVAAAAA8Pzw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PDw8PA==',
};

// Enhanced fallback beep using Web Audio API
export const playFallbackBeep = (type: 'success' | 'error' | 'warning' | 'info' | 'default' = 'default'): void => {
  try {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) {
      console.log('Web Audio API not supported');
      return;
    }
    
    const audioContext = new AudioContextClass();
    
    // Create oscillator for beep
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    // Configure sound based on type
    switch (type) {
      case 'success':
        oscillator.frequency.value = 880; // Higher pitch
        gainNode.gain.value = 0.2;
        break;
      case 'error':
        oscillator.frequency.value = 440; // Lower pitch
        gainNode.gain.value = 0.25;
        break;
      case 'warning':
        oscillator.frequency.value = 660; // Medium pitch
        gainNode.gain.value = 0.2;
        break;
      case 'info':
        oscillator.frequency.value = 550; // Medium pitch
        gainNode.gain.value = 0.15;
        break;
      default:
        oscillator.frequency.value = 800;
        gainNode.gain.value = 0.2;
    }
    
    oscillator.start();
    oscillator.stop(audioContext.currentTime + 0.2);
    
    console.log(`✅ Fallback ${type} beep played`);
  } catch (error) {
    console.log('Fallback sound failed:', error);
    
    // Last resort: try vibration (mobile)
    if (navigator.vibrate) {
      navigator.vibrate(type === 'error' ? 300 : 200);
    }
  }
};

// Get appropriate message based on notification type
export const getNotificationMessage = (type: string): string => {
  switch (type) {
    case 'order_accepted':
      return 'Your order has been accepted by a rider';
    case 'offer_accepted':
      return 'Your quick offer has been accepted';
    case 'payment_received':
      return 'Payment has been received';
    case 'service_started':
      return 'Service has started';
    case 'destination_reached':
      return 'Destination reached';
    case 'order_completed':
      return 'Order completed successfully';
    case 'new_message':
      return 'You have a new message';
    case 'offer_created':
      return 'Your offer has been posted';
    case 'system':
      return 'System notification';
    default:
      return 'You have a new notification';
  }
};

// Get appropriate title based on notification type
export const getNotificationTitle = (type: string): string => {
  switch (type) {
    case 'order_accepted':
      return '✅ Order Accepted';
    case 'offer_accepted':
      return '🎯 Offer Accepted';
    case 'payment_received':
      return '💰 Payment Received';
    case 'service_started':
      return '🚴 Service Started';
    case 'destination_reached':
      return '🏁 Destination Reached';
    case 'order_completed':
      return '🎉 Order Completed';
    case 'new_message':
      return '💬 New Message';
    case 'offer_created':
      return '📢 Offer Posted';
    case 'system':
      return '🔔 System Notification';
    default:
      return 'Notification';
  }
};

// Enhanced toast configuration with sounds
export const getToastConfig = (type: string, message: string) => {
  const baseConfig = {
    duration: 5000,
    style: {
      background: '#fff',
      color: '#363636',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      borderRadius: '12px',
      padding: '12px 16px',
    },
  };

  switch (type) {
    case 'order_accepted':
      return {
        ...baseConfig,
        icon: '✅',
        duration: 6000,
        style: { ...baseConfig.style, borderLeft: '4px solid #10b981' },
      };
    case 'offer_accepted':
      return {
        ...baseConfig,
        icon: '🎯',
        duration: 6000,
        style: { ...baseConfig.style, borderLeft: '4px solid #ec4899' },
      };
    case 'payment_received':
      return {
        ...baseConfig,
        icon: '💰',
        duration: 6000,
        style: { ...baseConfig.style, borderLeft: '4px solid #f59e0b' },
      };
    case 'service_started':
      return {
        ...baseConfig,
        icon: '🚴',
        duration: 6000,
        style: { ...baseConfig.style, borderLeft: '4px solid #3b82f6' },
      };
    case 'destination_reached':
      return {
        ...baseConfig,
        icon: '🏁',
        duration: 6000,
        style: { ...baseConfig.style, borderLeft: '4px solid #8b5cf6' },
      };
    case 'order_completed':
      return {
        ...baseConfig,
        icon: '🎉',
        duration: 6000,
        style: { ...baseConfig.style, borderLeft: '4px solid #10b981' },
      };
    case 'new_message':
      return {
        ...baseConfig,
        icon: '💬',
        duration: 8000, // Longer for messages
        position: 'bottom-right' as const,
        style: { ...baseConfig.style, borderLeft: '4px solid #6b7280' },
      };
    default:
      return {
        ...baseConfig,
        icon: '🔔',
        style: { ...baseConfig.style, borderLeft: '4px solid #6b7280' },
      };
  }
};

// Map notification types to sound types for fallback
export const getSoundType = (notificationType: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
  const successTypes = ['order_accepted', 'offer_accepted', 'payment_received', 'order_completed'];
  const errorTypes = ['order_cancelled', 'payment_failed'];
  const warningTypes = ['system', 'warning'];
  const infoTypes = ['new_message', 'service_started', 'destination_reached', 'offer_created'];
  
  if (successTypes.includes(notificationType)) return 'success';
  if (errorTypes.includes(notificationType)) return 'error';
  if (warningTypes.includes(notificationType)) return 'warning';
  if (infoTypes.includes(notificationType)) return 'info';
  return 'default';
};